import { PERM } from '@iss/libs/permissions';
import {
  AuthorizeComponent,
  ifHasRoleGuard,
  isAuthenticatedGuard,
  LoginComponent,
  resolveAuthEmail,
} from '@iss/libs/auth';
import { RouterModule, Routes, TitleStrategy } from '@angular/router';
import { NgModule } from '@angular/core';

import { LogoutComponent } from '../modules/logout';
import { hasRequiredContextGuard, hasRequiredSupplierContextGuard, isAuthorizedGuard } from '../guards';

import { AppRoutingPaths } from './app-routing.paths';

import { AppTitleStrategy, PageTitleResolver } from '@iss/wo-common/routing';
import { AccountComponent } from '@iss/wo-common/components';
import { GlobalErrorPageComponent, NotAuthorizedPageComponent, NotFoundPageComponent } from '@iss/core/portal';

const routes: Routes = [
  {
    path: '',
    canActivate: [ isAuthenticatedGuard ],
    children: [
      {
        path: AppRoutingPaths.AUTHORIZE,
        component: AuthorizeComponent,
        redirectTo: '',
      },
      {
        path: '',
        canActivate: [ isAuthorizedGuard ],
        children: [
          {
            path: AppRoutingPaths.APP_CONTEXT,
            loadChildren: () => import('../modules/app-context'),
          },
          {
            path: '',
            canActivate: [ hasRequiredContextGuard ],
            children: [
              {
                path: AppRoutingPaths.ORDERS,
                loadChildren: () => import('../modules/orders'),
              },
              {
                path: AppRoutingPaths.UPLOAD_WEIGHTS,
                loadChildren: () => import('../modules/upload-weight'),
                canActivate: [ hasRequiredSupplierContextGuard, ifHasRoleGuard(PERM['can manage upload weight']) ],
              },
              {
                path: AppRoutingPaths.SUPPLIERS,
                loadChildren: () => import('../modules/suppliers'),
                canActivate: [ ifHasRoleGuard(PERM['can manage suppliers']) ],
              },
              {
                path: AppRoutingPaths.CUSTOMERS,
                loadChildren: () => import('../modules/customers'),
              },
              {
                path: AppRoutingPaths.INVOICING,
                loadChildren: () => import('../modules/invoicing'),
                canActivate: [ ifHasRoleGuard(PERM['can manage invoicing']) ],
              },
              {
                path: AppRoutingPaths.REPORTS,
                loadChildren: () => import('../modules/reports'),
                canActivate: [ ifHasRoleGuard(PERM['can manage reports']) ],
              },
              {
                path: AppRoutingPaths.NEW_ORDER,
                pathMatch: 'full',
                loadChildren: () => import('../modules/orders/new-order'),
                canActivate: [ ifHasRoleGuard(PERM['can create a new order']) ],
              },
              {
                path: AppRoutingPaths.ADMIN,
                loadChildren: () => import('../modules/admin'),
                canActivate: [ ifHasRoleGuard(PERM['can manage admin page']) ],
              },
              {
                path: AppRoutingPaths.ACCOUNT,
                component: AccountComponent,
                title: PageTitleResolver,
                data: {
                  title: 'page.account',
                },
              },
              {
                path: '',
                redirectTo: AppRoutingPaths.ORDERS,
                pathMatch: 'full',
              },
            ],
          },
        ],
      },
      {
        path: AppRoutingPaths.CONSENT_APPROVAL,
        loadChildren: () => import('@iss/wo-common/modules/consent-approval'),
      },
      {
        path: AppRoutingPaths.GLOBAL_ERROR,
        component: GlobalErrorPageComponent,
      },
      {
        path: AppRoutingPaths.NOT_AUTHORIZED,
        component: NotAuthorizedPageComponent,
      },
    ],
  },
  {
    path: AppRoutingPaths.LOGOUT,
    component: LogoutComponent,
  },
  {
    path: AppRoutingPaths.LOGIN,
    component: LoginComponent,
    resolve: {
      email: resolveAuthEmail,
    },
  },
  {
    path: '**',
    component: NotFoundPageComponent,
  },
];

@NgModule({
  imports: [ RouterModule.forRoot(routes, { bindToComponentInputs: true, paramsInheritanceStrategy: 'always' }) ],
  exports: [ RouterModule ],
  providers: [
    {
      provide: TitleStrategy,
      useClass: AppTitleStrategy,
    },
    PageTitleResolver,
  ],
})
export class AppRoutingModule {}
